import type { Scene } from '../types'

export const challengeScenes: Scene[] = [
  {
    id: 'CH-01',
    chapter: 'challenge',
    route: '/challenge/proposition',
    title: 'Executive Proposition',
    headline: 'One land lifecycle. One operational environment.',
    supporting:
      'LAMS unifies the operational context of land — from evaluation and acquisition through stewardship and, when applicable, disposition — while preserving GIS, documents, work, approvals and history around the same business context.',
    visual: 'lifecycle-overview',
    callouts: [
      { label: 'Acquisition', detail: 'Establishes the land and business context' },
      { label: 'Land Management', detail: 'Preserves and expands operational history' },
      { label: 'Disposition', detail: 'Reuses that context rather than recreating it' },
    ],
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 1, p.2; Section 5, pp.6-8' },
      { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Section 2.1' },
    ],
    presenterNotes: [
      'This opens the project vision. It is the whole proposal in one screen — if an evaluator remembers one thing, it should be this.',
      'Connect it to what was just shown: the continuity of one Property across the lifecycle is the EAM discipline expressed as a business outcome.',
      'Stress continuity: the same Property record carries through all three domains.',
      'The cross-cutting layer is the point — GIS, work and documents are not separate systems.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 1 — Purpose and Project Overview', 'Section 5 — Scope of Work'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'What "one operational environment" means in practice',
        bullets: [
          'A single web-based interface across the three functional modules the RFP defines.',
          'One Property record that persists across acquisition, management and disposition.',
          'GIS context available inside the business workflow, not in a separate destination.',
          'Documents, work, approvals and history attached to the business record that created them.',
          'External systems that remain authoritative continue to do so; LAMS integrates rather than duplicates.',
        ],
        sources: [
          { kind: 'proposal', label: 'Merlion Proposed Solution', locator: 'Sections 2.1, 6.1' },
        ],
      },
    ],
  },

  {
    id: 'CH-02',
    chapter: 'challenge',
    route: '/challenge/mission',
    title: 'What SRWMD Is Solving',
    headline: 'Modernize the complete land lifecycle.',
    supporting:
      'The District is seeking to modernize and automate the end-to-end lifecycle of land assets, from initial acquisition applications through long-term stewardship to disposition.',
    visual: 'outcome-cards',
    callouts: [
      { label: 'Centralize', detail: 'Replace manual processes with one web-based interface' },
      { label: 'Automate', detail: 'Electronic submissions, workflow approvals, task assignment' },
      { label: 'Geospatially enable', detail: 'Integrate ESRI GIS; real-time geospatial visualization' },
      { label: 'Improve traceability', detail: 'Reliable reporting in place of spreadsheets and PDFs' },
    ],
    whyItMatters:
      'The District named a 40% reduction in manual task time as an objective of the automation. The Questions & Answers clarify that this is an automation objective, not a required measured task.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 1, p.2' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q91, p.11' },
    ],
    presenterNotes: [
      'Read the District\'s own objective back to them. This establishes that we understood the ask.',
      'Be precise about the 40%: the Q&A calls it an objective based on the automation, not a required measured task. Overclaiming here is a credibility risk.',
      'Do not list features yet. This scene is about the District\'s goal.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: ['Section 1 — Purpose and Project Overview'],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'The District\'s stated objective, in full',
        body: [
          'The RFP states that the District is soliciting a software solution to modernize and automate the end-to-end lifecycle of land assets, from initial acquisition applications to long-term stewardship to disposition. The project aims to replace manual processes with a centralized system capable of handling electronic submissions, complex workflow approvals, personnel task assignment tracking, and data-driven land management planning, management of ownership, lease agreements, and reporting. The solution must integrate with ESRI GIS technologies.',
        ],
        sources: [{ kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Section 1, p.2' }],
      },
      {
        title: 'On the 40% figure',
        body: [
          'A proposer asked what baseline metric applied, over what period, and how the 40% would be measured and validated post go-live. The District answered: "This is an objective based upon the automation, not a required measured task."',
          'LAMS is therefore designed to remove manual coordination work by construction — automated intake, generated documents, governed workflow routing and reusable land context — rather than to chase a contractually measured percentage.',
        ],
        sources: [{ kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q91, p.11' }],
      },
    ],
  },

  {
    id: 'CH-03',
    chapter: 'challenge',
    route: '/challenge/current-landscape',
    title: 'Current Operational Landscape',
    headline: 'The information exists. The operational context is fragmented.',
    supporting:
      'Each District system does its job well. The land process, however, crosses all of them — and every crossing is coordinated by a person.',
    visual: 'system-landscape',
    whyItMatters:
      'The cost is not in any one system. It is in the manual routing, re-keying and reconciliation required to move one land transaction across seven of them.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 1 Background, p.2; Section 5.A, p.6' },
      { kind: 'qa', label: 'Source: SRWMD Questions & Answers', locator: 'Q5, pp.1-2' },
    ],
    presenterNotes: [
      'Do not criticize the existing systems. Every one of them has value and several remain in place after LAMS.',
      'The argument is about the gaps between systems, not the systems themselves.',
      'Name the manual steps: routing documents through Windows network folders, re-keying between Access and GIS, assembling reports from spreadsheets and PDFs.',
    ],
    estimatedSeconds: 75,
    rfpAlignment: ['Section 1 — Background', 'Section 5.A — Shared Database Capabilities'],
    evalTags: ['EVAL-WP'],
    exploreDetails: [
      {
        title: 'Integration role of each system after LAMS',
        table: {
          head: ['System', 'Today', 'Role after LAMS'],
          rows: [
            [
              'Land Acquisition & Surplus Access database',
              '1,024 records across 30 fields; unknown parcel count',
              'Replaced. The District indicated it would ideally be replaced by this solution; its data is migrated into LAMS.',
            ],
            [
              'ArcGIS (Server 11.1 + ArcGIS Online)',
              'ArcGIS Desktop 10.8 and ArcGIS Pro 3.4 in use; ~314 tracts and ~1,640 parcels recorded',
              'Remains the District GIS ecosystem. LAMS consumes services and layers. Compatibility with ArcGIS Server 11.1 and ArcGIS Pro 3.4 is a District requirement.',
            ],
            [
              'CivicPlus',
              'Cloud-hosted government website platform; applications received here',
              'Retained. Application and form submissions flow into LAMS acquisition records through its API.',
            ],
            [
              'PaperVision Enterprise 87.5',
              'On-premises document repository, API available',
              'Retained as the document repository. LAMS searches, references and links documents through the API. Existing PaperVision documents are not migrated.',
            ],
            [
              'AccuFund',
              'Financial system',
              'Retained. Applicable financial information imported through scheduled CSV or another supported mechanism confirmed during discovery.',
            ],
            [
              'PERCH',
              'Custom on-premises database tracking Ag and Environmental Projects; no API',
              'Retained. Read-only access through District-provided SQL Server views.',
            ],
            [
              'Windows network folders',
              'Workflows routed by manually moving documents',
              'Replaced by governed workflow routing inside LAMS.',
            ],
          ],
        },
        sources: [
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q5, Q26' },
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3' },
        ],
      },
      {
        title: 'The District\'s integration rule',
        body: [
          'The District clarified that integrations possible via API may be bi-directional, but integrations only possible via file exports or direct database access must be read-only.',
          'LAMS respects this boundary by design. Its integration layer isolates each external dependency behind an adapter, so a read-only source stays read-only and a change to an external API, file format or database view does not reach the LAMS business model.',
        ],
        sources: [
          { kind: 'qa', label: 'SRWMD Questions & Answers', locator: 'Q5' },
          { kind: 'proposal', label: 'Merlion Proposed Architecture', locator: 'Section 6.3' },
        ],
      },
    ],
  },

  {
    id: 'CH-04',
    chapter: 'challenge',
    route: '/challenge/lifecycle',
    title: 'Three Business Domains, One Continuity',
    headline: 'Acquisition, Management and Disposition are not isolated modules.',
    supporting:
      'A single Property context travels through three lifecycle stages. The RFP itself asks for transitions between them, not for three separate systems.',
    visual: 'lifecycle-continuity',
    callouts: [
      { label: 'Acquisition establishes', detail: 'the land and business context' },
      { label: 'Management preserves and expands', detail: 'operational history against that context' },
      { label: 'Disposition reuses', detail: 'the accumulated record rather than recreating it' },
      { label: 'GIS, documents and work', detail: 'remain cross-cutting throughout' },
    ],
    whyItMatters:
      'The RFP requires the Acquisition module to transition applicable data to Land Management, and Land Management to transition applicable data to Disposition. Continuity is a stated requirement, not a design preference.',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Sections 5.B-5.D, pp.7-8' },
    ],
    presenterNotes: [
      'Point at the RFP\'s own "Transitioning Modules" requirement in both 5.B and 5.D. The District asked for continuity explicitly.',
      'This scene sets up why the Property object matters later. Do not explain the data model yet.',
    ],
    estimatedSeconds: 60,
    rfpAlignment: [
      'Section 5.B — Land Acquisition, Transitioning Modules',
      'Section 5.C — Land Management',
      'Section 5.D — Land Disposition, Transitioning Modules',
    ],
    evalTags: ['EVAL-WP'],
  },

  {
    id: 'CH-05',
    chapter: 'challenge',
    route: '/challenge/success',
    title: 'What Success Looks Like',
    headline: 'Less coordination work. More operational confidence.',
    supporting:
      'The measure of success is not a feature count. It is whether a District officer can answer a question about a tract without assembling the answer from six places.',
    visual: 'before-after',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Section 1, p.2; Section 5, pp.6-8' },
    ],
    presenterNotes: [
      'Frame in the District\'s language: the RFP names inefficient reporting, data inaccuracies and risks of missing compliance deadlines as the current cost.',
      'Keep this short. It is a hinge into the solution chapter.',
    ],
    estimatedSeconds: 50,
    rfpAlignment: ['Section 1 — Background', 'Section 5 — Scope of Work'],
    evalTags: ['EVAL-WP', 'EVAL-OUTCOME'],
  },

  {
    id: 'CH-06',
    chapter: 'challenge',
    route: '/challenge/alignment',
    title: 'RFP Alignment',
    headline: 'This proposal is organized around the District\'s evaluation priorities.',
    supporting:
      'Work Plan & Approach carries half the available score. It is therefore the deepest part of this presentation, and time and cost, staff and outcomes are each directly traceable.',
    visual: 'evaluation-weights',
    sourceRefs: [
      { kind: 'rfp', label: 'Source: SRWMD RFP 26/27-002', locator: 'Evaluation and Award, pp.5-6' },
    ],
    presenterNotes: [
      'Say plainly that every scoring criterion has a home in this proposal, and that Explore Mode lets each evaluator go straight to theirs.',
      'Point out where each criterion is addressed, then move on.',
    ],
    estimatedSeconds: 45,
    rfpAlignment: ['Evaluation and Award, pp.5-6'],
    evalTags: ['EVAL-WP', 'EVAL-TC', 'EVAL-STAFF', 'EVAL-OUTCOME'],
    exploreDetails: [
      {
        title: 'Where each evaluation criterion is addressed',
        table: {
          head: ['Criterion', 'Points', 'Where this proposal answers it'],
          rows: [
            [
              'Work Plan & Approach',
              '50',
              'EAM (7 scenes), Challenge and Solution (15 scenes), Products (4 scenes), Architecture (9 scenes) and Delivery (10 scenes) — including understanding of District needs, ability to meet project goals, software functionality and interface, network and hardware requirements, and identified risks with mitigation.',
            ],
            [
              'Time and Cost Proposal',
              '20',
              'Delivery — Scrum method, 34-week schedule, sprint roadmap and M1-M9 milestones. Investment — not-to-exceed price, milestone payment structure, financial consequences for missed deadlines, and the separate technical-support price.',
            ],
            [
              'Staff Qualifications',
              '15',
              'Team — the five named professionals with assigned roles, alignment to the project, domain expertise, certifications, practical EAM experience and the documented record of projects this team has delivered together, supported by Merlion IT\'s corporate leadership.',
            ],
            [
              'Outcomes and Performance Measurements',
              '15',
              'Delivery — outcomes and performance measures, migration reconciliation, testing and UAT, training and technical support. Investment — accountability commitments. Architecture — controlled evolution after go-live.',
            ],
          ],
        },
        sources: [{ kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Evaluation and Award, pp.5-6' }],
      },
      {
        title: 'What the District asked for under Work Plan & Approach',
        bullets: [
          'Understanding of the District\'s needs.',
          'Ability to meet District\'s project goals.',
          'Software functionality, interface, network and any hardware requirements.',
          'Identified risks and constraints with mitigation recommendations.',
          'Define any use of AI in the creation of a proposal, software solution, or continuous improvement plan.',
        ],
        sources: [{ kind: 'rfp', label: 'SRWMD RFP 26/27-002', locator: 'Evaluation and Award, pp.5-6' }],
      },
    ],
  },
]
